import { z } from "zod";
import type { CreateMistakeReviewSetResult } from "~/server/mistakeReview/savedSet";

const availability = z.object({ available: z.boolean() });
const unknownCreationOutcome =
  "Результат создания набора неизвестен. Проверьте список наборов перед повторной отправкой.";
const creation: z.ZodType<CreateMistakeReviewSetResult> = z.object({
  problemSetId: z.number().int().positive(),
  firstProblemId: z.number().int().positive(),
  name: z.string(),
  visibility: z.enum(["public", "private", "hidden"]),
  generatedTaskCount: z.number().int().positive(),
  includedSourceCount: z.number().int().positive(),
  skippedUnusableSourceCount: z.number().int().nonnegative(),
});

async function request(problemSetId: number, init: RequestInit) {
  let response: Response;
  let body: unknown;
  try {
    response = await fetch(
      `/api/problem-sets/${problemSetId}/mistake-review`,
      init,
    );
    body = await response.json();
  } catch (cause) {
    const detail =
      cause instanceof Error ? cause.message : "Не удалось выполнить запрос.";
    throw new Error(
      init.method === "POST"
        ? `${unknownCreationOutcome} ${detail}`
        : "Не удалось проверить доступность.",
    );
  }
  if (!response.ok) {
    const error = z.object({ message: z.string() }).safeParse(body);
    throw new Error(
      error.success ? error.data.message : "Не удалось выполнить запрос.",
    );
  }
  return body;
}

export async function fetchMistakeReviewAvailability(
  problemSetId: number,
  signal: AbortSignal,
) {
  const result = availability.safeParse(
    await request(problemSetId, { cache: "no-store", signal }),
  );
  if (!result.success) throw new Error("Не удалось проверить доступность.");
  return result.data.available;
}

export async function createMistakeReview(
  problemSetId: number,
  input: { title: string; visibility: string },
) {
  const result = creation.safeParse(
    await request(problemSetId, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }),
  );
  if (!result.success) throw new Error(unknownCreationOutcome);
  return result.data;
}
